import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import { DatePeriod } from '../../../../../../features/date-time/date-picker/date-picker-calendar/use-date-picker-calendar';
import { LangEnum } from '../../../../../../helpers/lang';
import { useLang } from '../../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import i18n, { dayjsLocales } from '../../../../../../i18n/i18n';
import { defaultShiftsPeriod, defaultShiftsRange } from '../shifts-helpers';

dayjs.extend(weekday);
const format = 'YYYY-MM-DD HH:mm';

const getDisplayRange = (range = dayjs(new Date()).format(format), period: DatePeriod) => {
  const displayFormat = 'DD MMM YYYY';

  const locale = dayjsLocales[i18n.language];

  if (period === 'week') {
    const end = dayjs(range).add(6, 'day');
    return `${dayjs(range).locale(locale).format('DD MMM')} - ${dayjs(end)
      .locale(locale)
      .format('DD MMM')}`;
  }
  if (period === 'month') {
    return dayjs(range).locale(locale).format('MMM YYYY');
  }
  return dayjs(range).locale(locale).format(displayFormat);
};

const getRange = (period: DatePeriod, date?: Date) => {
  const today = dayjs(date || new Date());

  if (period === 'day') return today.format(format);

  const start = today.startOf(period);

  if (period === 'week' && i18n.language === LangEnum.EN) return start.weekday(1).format(format);

  return start.format(format);
};

export const useShiftsDates = () => {
  const { t } = useLang();
  const { addQueryParams, getQueryParams } = useQueryParams();
  const params = getQueryParams();

  const period = (params.period || defaultShiftsPeriod) as DatePeriod;
  const range = params.range || defaultShiftsRange;
  const displayRange = getDisplayRange(range, period);

  const handleChangePeriod = (period: DatePeriod) => {
    addQueryParams({ period, range: getRange(period) });
  };

  const selectedDate = new Date(range);

  const handleNext = () => {
    const nextVal = dayjs(selectedDate).add(1, period).toDate();
    addQueryParams({ range: getRange(period, nextVal) });
  };

  const handlePrev = () => {
    const prevVal = dayjs(selectedDate).subtract(1, period).toDate();
    addQueryParams({ range: getRange(period, prevVal) });
  };

  const handleSelectDate = (date: Date) => {
    addQueryParams({ range: getRange(period, date) });
  };

  return {
    t,
    period,
    displayRange,
    handleChangePeriod,
    handleNext,
    handlePrev,
    handleSelectDate,
    selectedDate,
  };
};
